import { Card } from "../ui/Card";

interface MonthlyEnrollment {
  _id?: string;
  month?: string;
  count: number;
  year?: number;
}

interface ChartsSectionProps {
  monthlyEnrollments?: MonthlyEnrollment[];
  isLoading?: boolean;
}

const formatMonthName = (item: MonthlyEnrollment) => {
  if (item.year && item.month) {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${monthNames[Number(item.month) - 1]} '${item.year.toString().slice(-2)}`;
  }
  return (
    item.month ||
    item._id ||
    `M${item.year ? item.year.toString().slice(-2) : ""}`
  );
};

export function ChartsSection({
  monthlyEnrollments = [],
  isLoading = false,
}: ChartsSectionProps) {
  const maxCount = Math.max(...monthlyEnrollments.map((d) => d.count || 0), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900">Enrollment Trends</h3>
        </div>
        <div className="h-64 relative">
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              Loading...
            </div>
          ) : monthlyEnrollments.length > 0 ? (
            <>
              <div className="absolute inset-0 flex items-end justify-between gap-4 px-4 pb-8">
                {monthlyEnrollments.map((item, index) => {
                  const heightPercent =
                    maxCount > 0 ? ((item.count || 0) / maxCount) * 100 : 0;
                  const count = item.count || 0;
                  return (
                    <div
                      key={index}
                      className="flex flex-col items-center justify-end flex-1 min-w-0 h-full relative"
                    >
                      {count > 0 && (
                        <div
                          className="w-11/12 max-w-16 bg-linear-to-t from-indigo-600 via-indigo-500 to-indigo-400 rounded-t-lg transition-all duration-300 hover:from-indigo-700 hover:via-indigo-600 hover:to-indigo-500 cursor-pointer relative group shadow-lg shadow-indigo-200"
                          style={{ height: `${Math.max(heightPercent, 0)}%` }}
                        >
                          <div className="absolute inset-0 bg-linear-to-b from-white/0 to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                          <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap pointer-events-none z-20 font-medium">
                            {count} enrollments
                          </div>
                        </div>
                      )}
                      <span className="text-xs text-gray-600 mt-2 text-center truncate w-full font-medium">
                        {formatMonthName(item)}
                      </span>
                      <span className="text-sm font-bold text-gray-900 text-center">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200" />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              No enrollment data available
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900">
            Enrollment Summary
          </h3>
        </div>
        <div className="h-[300px] w-full flex flex-col items-center justify-center gap-4 p-4">
          {isLoading ? (
            <div className="text-gray-400">Loading...</div>
          ) : (
            <>
              <div className="text-center">
                <p className="text-4xl font-bold text-blue-600">
                  {monthlyEnrollments.reduce(
                    (sum, item) => sum + (item.count || 0),
                    0,
                  )}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Total Enrollments (Last 6 Months)
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4 w-full max-w-md">
                {monthlyEnrollments.slice(-3).map((item, index) => {
                  return (
                    <div
                      key={index}
                      className="text-center p-4 bg-linear-to-br from-gray-50 to-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
                    >
                      <p className="text-lg font-bold text-gray-900">
                        {item.count || 0}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatMonthName(item)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
